import { useFormik } from "formik";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Button from "src/components/buttons/Button";
import StatusCard from "src/components/cards/StatusCard";
import Input from "src/components/inputs/Input";
import useNotification from "src/hooks/useNotification";
import { object, string } from "yup";
import { useForgotPassword } from "../api-client";
import PublicLayout from "../PublicLayout";

const FORM_FIELDS = {
  EMAIL: "email",
  PASSWORD: "password",
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { errorNotification } = useNotification();
  const { execute, isLoading, isSuccess, error } = useForgotPassword();
  const { values, errors, handleSubmit, setFieldValue, touched } = useFormik({
    initialValues: {
      [FORM_FIELDS.EMAIL]: "",
    },
    validationSchema: object({
      [FORM_FIELDS.EMAIL]: string()
        .email("Please enter valid email.")
        .required("This is a Required field."),
    }),
    onSubmit: ({ email }) => {
      execute({ email });
    },
  });

  useEffect(() => {
    if (error) {
      const errorStatus = error?.cause?.status;
      if (errorStatus === 404) {
        errorNotification("Account doesn't exist.");
      } else {
        errorNotification();
      }
    }
  }, [error, errorNotification]);

  const isSuccessFul = useMemo(() => isSuccess && !error, [error, isSuccess]);

  return (
    <PublicLayout
      leftSection={{
        heading: "Forgot Your Password ? We've Got You Covered!",
        features: [
          {
            text: "Can't remember your password? No worries! Enter your registered email to reset it easily.",
          },
          {
            text: "We're here to help you get back into your account quickly and securely.",
          },
        ],
      }}
      rightSection={
        isSuccessFul
          ? {}
          : {
              heading: "Forgot Password",
              subHeading:
                "Enter your email and we'll send you a link to reset your password.",
            }
      }
    >
      {isSuccessFul ? (
        <StatusCard
          title="Check Your Email."
          subtitle="Please check the email for instruction to reset your password."
        />
      ) : (
        <div className="flex flex-col">
          <form
            onSubmit={handleSubmit}
            className="flex flex-col justify-start"
            noValidate
          >
            <Input
              type="email"
              label="Enter Email"
              placeholder="Enter Email"
              name={FORM_FIELDS.EMAIL}
              value={values[FORM_FIELDS.EMAIL]}
              error={
                touched[FORM_FIELDS.EMAIL]
                  ? errors[FORM_FIELDS.EMAIL]
                  : undefined
              }
              onChange={(e) => setFieldValue(FORM_FIELDS.EMAIL, e.target.value)}
              disabled={isLoading}
              required
            />

            <Button
              className={"mt-6 w-[70%]"}
              type="submit"
              text={isLoading ? "Loading..." : "Submit"}
              primary
            />
          </form>

          <p className="mt-[15px] text-left text-sm text-gray-500 gap-2">
            Already have an account ?{" "}
            <span
              onClick={() => navigate("/login")}
              className="font-semibold leading-6 text-primary hover:text-primary-800 cursor-pointer"
            >
              Sign In
            </span>
          </p>
        </div>
      )}
    </PublicLayout>
  );
};

export default ForgotPassword;
